import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { CheckCircle2, CircleDashed, MapPinOff, HelpCircle, Loader2 } from "lucide-react";
import { formatISTDayLabel } from "../../lib/datetime";
import { count, joinParts, NO_VALUE, plural } from "../../lib/vocabulary";
import { DOCTORS_NOT_NAMED, DOCTORS_NOT_NAMED_HELP } from "./plannedDoctors";

/**
 * Plan vs actual — what the plan named, against what the DCRs say happened.
 *
 * THE WORDS ARE THE POINT (Pattern A, `lib/vocabulary.js`). A planned doctor
 * with no DCR is "No visit recorded", never "Missed": the database knows that
 * nobody filed a visit report, and it does not know the officer did not go.
 * The two sentences are one word apart and land in a performance conversation.
 *
 * THREE BUCKETS, AND A FOURTH THAT REFUSES TO BE ONE. A visit on a day whose
 * plan entry never named any doctors cannot be called unplanned — nothing is
 * known about what was planned for it. Those are shown apart, with the days
 * listed, rather than counted against the officer.
 *
 * `comparable` IS NOT "THIS PLAN HAS DOCTORS". A plan with no days at all is
 * comparable: it planned nobody, that is a fact, and its month's visits really
 * are unplanned. The gap needs a day that EXISTS and named nobody — otherwise
 * every untouched month would open shouting about a gap.
 *
 * NOTHING IS DERIVED HERE. Every count comes from the endpoint, which is the
 * side that can distinguish `null` from `0`; this file only chooses words.
 */
export default function PlanVsActual({ planId }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api.get(`/tour-plans/${planId}/vs-actual`)
      .then(({ data }) => !cancelled && setReport(data))
      .catch((e) => !cancelled &&
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [planId]);

  if (loading) {
    return (
      <div className="bg-white border border-callisto rounded-xl p-5 md:p-7 flex items-center gap-2 text-sm text-callisto-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading plan vs actual…
      </div>
    );
  }
  if (error || !report) {
    // The comparison is UNAVAILABLE, which is not the same as "nothing to
    // compare" — an empty panel here would read as a clean month.
    return (
      <div className="bg-white border border-callisto rounded-xl p-5 md:p-7" data-testid="plan-vs-actual-error">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-2">
          Plan vs actual
        </p>
        <p className="text-sm text-callisto-muted">
          This comparison could not be loaded, so nothing about it is being shown.
          {error ? ` (${error})` : ""}
        </p>
      </div>
    );
  }

  const c = report.counts || {};
  return (
    <div className="bg-white border border-callisto rounded-xl p-5 md:p-7" data-testid="plan-vs-actual">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold">
          Plan vs actual
        </p>
        <p className="text-[11px] text-callisto-muted" data-testid="plan-vs-actual-source">
          {report.compared_against === "approved_baseline"
            ? "Compared against the plan as approved"
            : "Compared against the current draft — this plan has no approved copy"}
        </p>
      </div>

      {!report.comparable ? (
        // THE GAP, SHOWN AS A GAP. No tiles, because every one of them would be
        // a number derived from a field nobody ever wrote.
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5"
             data-testid="plan-vs-actual-gap">
          <p className="text-sm font-semibold text-amber-900">{DOCTORS_NOT_NAMED}</p>
          <p className="mt-0.5 text-[12px] text-amber-800">{DOCTORS_NOT_NAMED_HELP}</p>
          <p className="mt-1.5 text-[12px] text-amber-800">
            {plural(c.visits_in_month ?? 0, "visit")} recorded this month
            {c.visits_in_month ? ", none of which can be called planned or unplanned." : "."}
          </p>
        </div>
      ) : (
        <>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Tile label="Planned" value={count(c.planned)} testId="pva-planned" />
            <Tile label="Visited" value={count(c.visited)} testId="pva-visited" />
            <Tile label="No visit recorded" value={count(c.no_visit_recorded)} testId="pva-no-visit" />
            <Tile label="Unplanned visits" value={count(c.unplanned)} testId="pva-unplanned" />
          </div>

          <Group
            icon={CheckCircle2}
            title="Visited as planned"
            rows={report.planned_visited}
            empty="No planned visit has a DCR against it."
            testId="pva-visited-list"
          />
          <Group
            icon={CircleDashed}
            title="No visit recorded"
            // The sentence that keeps this panel honest. It is here, not only
            // in the heading, because the heading is what gets screenshotted.
            note="Nobody filed a DCR for these. That is not a record that the visit did not happen."
            rows={report.planned_no_visit_recorded}
            empty="Every planned doctor has a visit recorded."
            testId="pva-no-visit-list"
          />
          <Group
            icon={MapPinOff}
            title="Unplanned visits"
            rows={report.unplanned_visits}
            empty="Every recorded visit was on the plan."
            testId="pva-unplanned-list"
          />
        </>
      )}

      {report.visits_not_comparable?.length > 0 && (
        <Group
          icon={HelpCircle}
          title="Not comparable"
          note={joinParts([
            "These visits fall on days the plan covers but never named doctors for",
            `${plural(report.days_without_named_doctors?.length || 0, "day")}: ` +
              (report.days_without_named_doctors || []).map(formatISTDayLabel).join(", "),
          ], " — ")}
          rows={report.visits_not_comparable}
          empty=""
          testId="pva-not-comparable-list"
        />
      )}

      {report.unresolved_doctor_ids?.length > 0 && (
        <p className="mt-3 text-[11px] text-amber-800" data-testid="pva-unresolved">
          {plural(report.unresolved_doctor_ids.length, "planned doctor")} no longer in the
          doctor master. They are still counted as planned — dropping them would flatter the
          comparison.
        </p>
      )}
    </div>
  );
}

function Tile({ label, value, testId }) {
  return (
    <div className="rounded-lg border border-callisto bg-callisto-app px-3 py-2" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wide text-callisto-muted">{label}</p>
      <p className="text-lg font-bold text-callisto-navy">{value}</p>
    </div>
  );
}

function Group({ icon: Icon, title, note, rows, empty, testId }) {
  const list = rows || [];
  return (
    <div className="mt-4" data-testid={testId}>
      <p className="text-xs font-bold text-callisto-navy flex items-center gap-1.5">
        <Icon size={13} /> {title} · {list.length}
      </p>
      {note && <p className="mt-0.5 text-[11px] text-callisto-muted">{note}</p>}
      {list.length === 0 ? (
        empty ? <p className="mt-1 text-[11px] text-callisto-muted">{empty}</p> : null
      ) : (
        <ul className="mt-1.5 divide-y divide-callisto border border-callisto rounded-lg">
          {list.map((r, i) => (
            <li key={`${r.date}-${r.doctor_id}-${i}`} className="px-3 py-1.5 flex items-baseline justify-between gap-2">
              {/* A doctor the master no longer answers for reads as absent, and
                  is never given an invented name or silently dropped. */}
              <span className="text-xs text-callisto-navy truncate">
                {r.doctor_name || <span className="text-callisto-muted italic">Doctor not in the master ({NO_VALUE})</span>}
              </span>
              <span className="text-[11px] text-callisto-muted shrink-0">{formatISTDayLabel(r.date)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
